import * as Sentry from '@sentry/react';
import { useEffect, type ReactElement } from 'react';
import { useRouteError } from 'react-router-dom';

// Global error fallback for the panel. Two consumers:
//
//   1. <RouteErrorBoundary/> — wired as `errorElement` on the authenticated route tree
//      (src/pages/routes/router.tsx). React Router renders it instead of its bare default
//      "Unexpected Application Error" screen when any route/child throws during render or commit.
//   2. <Sentry.ErrorBoundary fallback={<AppErrorFallback/>}> in src/index.tsx — the outermost net
//      for the provider tree that lives ABOVE RouterProvider (Auth/PostHog/Chatwoot).
//
// Kept deliberately dependency-light (inline styles, no design-system imports): the whole point is
// to render even when something upstream is broken. A reload is offered because the most common
// recoverable cause is a stale Service Worker / chunk after a deploy.

export const AppErrorFallback = (): ReactElement => {
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
        background: '#f4f8ff',
        color: '#1b2733',
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}
    >
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Algo salió mal</h1>
      <p style={{ fontSize: 15, maxWidth: 420, margin: 0, lineHeight: 1.5 }}>
        Tuvimos un problema al cargar el panel. Vuelve a intentarlo; si el problema continúa, cierra y abre de nuevo la
        aplicación.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          marginTop: 8,
          padding: '12px 24px',
          fontSize: 15,
          fontWeight: 600,
          color: '#fff',
          background: '#1b2733',
          border: 'none',
          borderRadius: 10,
          cursor: 'pointer',
        }}
      >
        Recargar
      </button>
    </div>
  );
};

// React Router error boundary. Reports the captured route error to Sentry (errors caught by a
// boundary do NOT reach window.onerror, so without this they would be invisible) and renders the
// shared fallback.
export const RouteErrorBoundary = (): ReactElement => {
  const error = useRouteError();
  useEffect(() => {
    Sentry.captureException(error, {
      level: 'error',
      tags: { feature: 'app_shell', action: 'route_error_boundary' },
    });
  }, [error]);
  return <AppErrorFallback />;
};
